import { useState } from "react";
import Cookies from "js-cookie";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../redux/store";
import { setSystemUserToMessenger, getRecipients } from "../redux/reducers/messengerSlice";
import { login, fetchLabels } from "../redux/reducers/authSlice";
import { redirectIfMigrated } from "../Utils/migrationHandoff";
import { TextField } from "@/subframe/components/TextField";
import { Button } from "@/subframe/components/Button";

type FormField = "email" | "password";

// Define form data type
type FormData = {
  email: string;
  password: string;
};

// Define errors type
type FormErrors = {
  email: string;
  password: string;
};

function SignIn() {
  const dispatch = useDispatch<AppDispatch>();
  const [formData, setFormData] = useState<FormData>({
    email: "",
    password: "",
  });

  // Validation state
  const [errors, setErrors] = useState<FormErrors>({
    email: "",
    password: "",
  });

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [apiError, setApiError] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Type assertion to ensure name is a valid key
    const fieldName = name as FormField;
    const normalizedValue =
      fieldName === "email" ? value.toLowerCase() : value;

    setFormData({
      ...formData,
      [fieldName]: normalizedValue,
    });

    if (errors[fieldName]) {
      setErrors({
        ...errors,
        [fieldName]: "",
      });
    }

    if (apiError) {
      setApiError("");
    }
  };

  // Validate form inputs
  const validateForm = () => {
    let isValid = true;
    const newErrors = { email: "", password: "" };

    if (!formData.email) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = "Password is required";
      isValid = false;
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setApiError("");

    if (validateForm()) {
      setIsLoading(true);

      try {
        // Make API request using fetch instead of axios
        const response = await fetch("https://api.insightssystem.com/api:-4GSCDHb/auth/login", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: formData.email.toLowerCase(),
            password: formData.password,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || data.message || "Login failed");
        }

        // Handle successful login
        if (data && data.authToken) {
          // 1. Set authToken as es_chat cookie
          Cookies.set("es_chat", data.authToken, { expires: 7 });
          
          // 2. Call /auth/me to get user data using the token directly
          try {
            const response = await fetch('https://api.insightssystem.com/api:-4GSCDHb/auth/me', {
              method: 'GET',
              headers: {
                'Authorization': data.authToken,
                'Content-Type': 'application/json',
              },
            });

            if (response.ok) {
              const userData = await response.json();

              // A migrated tenant is served by the new system. Check before this app's state is
              // populated, so nobody is briefly signed into an inbox they are about to leave.
              //
              // Wrapped on its own: a throw here must not land in the outer catch below and be
              // reported as a failed login. The login already succeeded and the es_chat cookie is
              // already set, so on a throw we fail open and continue as if the check had answered
              // "not migrated".
              try {
                if (await redirectIfMigrated(data.authToken)) {
                  return;
                }
              } catch {
                // fail open: continue into the legacy app below
              }

              const role = userData.role ?? null;
              const userLabels = Array.isArray(userData.labels) ? userData.labels : [];
              dispatch(login({
                system_user_id: userData.user_id,
                role,
                userLabels,
                name: typeof userData.name === "string" ? userData.name : null,
              }));
              dispatch(setSystemUserToMessenger(userData.user_id));
              
              // Fetch data
              setTimeout(() => {
                dispatch(getRecipients({ page: 1 }));
                dispatch(fetchLabels());
              }, 100);
            } else {
              setApiError("Failed to get user data after login");
            }
          } catch (error) {
            setApiError("Failed to get user data after login");
          }
        } else {
          setApiError("Invalid response from server");
        }
      } catch (error: unknown) {
        if (error instanceof Error) {
          setApiError(error.message);
        } else {
          setApiError("An error occurred. Please try again later.");
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  //Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  return (
    <div className="container max-w-none flex h-full w-full flex-col items-center justify-center gap-4 bg-brand-100">
      <form
        onSubmit={handleSubmit}
        className="flex w-full max-w-[384px] flex-col items-center justify-center gap-7 rounded-lg border border-solid border-neutral-border bg-white px-12 pt-9 pb-8 shadow-md mobile:h-auto mobile:w-full mobile:max-w-[320px] mobile:flex-none"
      >
        <div className="flex w-full flex-col items-center gap-4">
          <img
            className="w-12 flex-none"
            src="https://res.cloudinary.com/subframe/image/upload/v1732870928/uploads/4086/khdrdhexuo94bjzfwcz4.svg"
            alt="Logo"
          />
          <div className="flex flex-col items-center gap-2">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Chat Notifyer
            </span>
            <span className="text-body font-body text-subtext-color">
              User login to Chat Notifyer
            </span>
          </div>
        </div>

        {/* Show API error if any */}
        {apiError && (
          <div className="w-full p-2 bg-red-100 text-red-700 rounded-md text-sm text-center">
            {apiError}
          </div>
        )}

        <div className="flex w-full flex-col items-start gap-6">
          <div className="flex w-full flex-col items-start gap-4">
            <TextField
              className="h-auto w-full flex-none"
              variant="outline"
              label="Username/Email"
              helpText={errors.email || ""}
              error={!!errors.email}
              icon="FeatherUser"
            >
              <TextField.Input
                name="email"
                placeholder="Enter your username or email..."
                value={formData.email}
                onChange={handleChange}
              />
            </TextField>

            <div className="relative w-full">
              <TextField
                className="h-auto w-full flex-none"
                variant="outline"
                label="Password"
                helpText={errors.password || ""}
                error={!!errors.password}
                icon="FeatherLock"
              >
                <TextField.Input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your secure password..."
                  value={formData.password}
                  onChange={handleChange}
                  className="pr-10" // Add padding for the icon
                />
              </TextField>
              <div
                className="absolute right-2 top-8 -translate-y-1/4 cursor-pointer text-gray-500 hover:text-gray-700"
                onClick={togglePasswordVisibility}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </div>
            </div>
          </div>
        </div>

        <Button
          type="submit"
          className="h-10 w-full flex-none"
          size="large"
          disabled={isLoading}
        >
          {isLoading ? "Signing in..." : "Sign In"}
        </Button>
      </form>

      <div className="flex w-96 items-center gap-2 mobile:h-auto mobile:w-80 mobile:flex-none">
        <div className="flex h-px grow shrink-0 basis-0 flex-col items-center gap-2 bg-subtext-color" />
        <span className="text-body font-body text-subtext-color">
          Want to login as Admin
        </span>
        <div className="flex h-px grow shrink-0 basis-0 flex-col items-center gap-2 bg-subtext-color" />
      </div>

      <Button
        className="mobile:w-52"
        variant="brand-secondary"
        size="large"
        onClick={() =>
          window.open(
            "https://console.notifyer-systems.com",
            "_blank",
            "noopener,noreferrer"
          )
        }
      >
        Login to Console
      </Button>
    </div>
  );
}

export default SignIn;
